<template>
  <app-page labelledby="account-cards-title" full-width>
    <page-header
      title-id="account-cards-title"
      title="Карточки счетов"
      description="Выберите счет, чтобы открыть его движения и сальдо за период."
    />

    <app-data-table
      title="Счета"
      :count="filteredRows.length"
      :rows="filteredRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      storage-key="finance-account-cards"
      data-testid="account-card-selector"
      @retry="loadAccounts"
      @reset="resetFilters"
    >
      <template #filters>
        <filter-bar
          v-model="search"
          search-label="Код или название счета"
          search-test-id="account-card-search"
          :has-filters="Boolean(search)"
          @reset="resetFilters"
        />
      </template>
      <template #body-cell-name="props">
        <q-td :props="props">
          <strong>{{ props.row.name || '—' }}</strong>
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props" class="text-right">
          <q-btn
            flat
            no-caps
            color="primary"
            label="Открыть карточку"
            :aria-label="`Открыть карточку счета ${props.row.full_name}`"
            :data-testid="`open-account-card-${props.row.id}`"
            @click="openCard(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>
  </app-page>
</template>

<script>
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import AppPage from '../../../components/ui/AppPage.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import { normalizeCompactBillsResponse } from '../../../services/financeCore'

const columns = Object.freeze([
  { name: 'name', label: 'Код', field: 'name', align: 'left', required: true, sortable: true },
  { name: 'full_name', label: 'Наименование', field: 'full_name', align: 'left', required: true, sortable: true },
  { name: 'description', label: 'Описание', field: 'description', align: 'left', classes: 'column-priority-secondary' },
  { name: 'actions', label: 'Действия', field: 'actions', align: 'right', required: true }
])

export default {
  name: 'AccountCardsSelector',
  components: { AppDataTable, AppPage, FilterBar, PageHeader },
  data() {
    return {
      columns,
      rows: [],
      loading: true,
      error: '',
      search: String(this.$route.query.search || ''),
      pagination: { page: 1, rowsPerPage: 25, sortBy: 'name', descending: false }
    }
  },
  computed: {
    filteredRows() {
      const query = this.search.trim().toLocaleLowerCase('ru-RU')
      if (!query) return this.rows
      return this.rows.filter((row) => [row.name, row.full_name, row.description]
        .some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(query)))
    },
    tableState() {
      if (this.loading) return 'loading'
      if (this.error) return 'error'
      if (!this.rows.length) return 'empty'
      if (!this.filteredRows.length) return 'filtered-empty'
      return 'ready'
    },
    stateTitle() {
      if (this.error) return 'Не удалось загрузить счета'
      if (!this.rows.length) return 'Счета не найдены'
      return ''
    },
    stateMessage() {
      return this.error || 'Измените поиск или вернитесь к полному списку.'
    }
  },
  watch: {
    search(value) {
      const query = { ...this.$route.query }
      if (value) query.search = value
      else delete query.search
      this.$router.replace({ query })
    }
  },
  created() {
    this.emitter.on('didReceiveBillListFlatCompact', this.onAccounts)
    this.loadAccounts()
  },
  beforeUnmount() {
    this.emitter.off('didReceiveBillListFlatCompact', this.onAccounts)
  },
  methods: {
    loadAccounts() {
      this.loading = true
      this.error = ''
      const request = () => this.connection.gitBillsListFlatCompact('account-cards-selector')
      if (this.connection.isAuthenticated) request()
      else {
        const ready = () => {
          this.emitter.off('connectionReady', ready)
          request()
        }
        this.emitter.on('connectionReady', ready)
      }
    },
    onAccounts(response) {
      const result = normalizeCompactBillsResponse(response)
      this.loading = false
      if (!result.ok) {
        this.rows = []
        this.error = result.message
        return
      }
      this.rows = result.bills
    },
    resetFilters() {
      this.search = ''
    },
    openCard(row) {
      this.$router.push({ name: 'bill_card', params: { id: row.id } })
    }
  }
}
</script>
